import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './component.css';

const Dashboard = () => {
    const [drmtlgyStats, setDrmtlgyStats] = useState({
        awaitingShipment: 0,
        onHold: 0,
        shipped: 0,
        cancelled: 0
    });
    const [kitschStats, setKitschStats] = useState({
        awaitingShipment: 0,
        onHold: 0,
        shipped: 0,
        cancelled: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [drmtlgyLastUpdate, setDrmtlgyLastUpdate] = useState('');
    const [kitschLastUpdate, setKitschLastUpdate] = useState('');

    const BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_DRMTLGY_BASE_URL || 'http://localhost:8080';

    const getAuthHeaders = () => ({
        'ngrok-skip-browser-warning': 'true',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
    });

    const fetchDrmtlgyCachedData = async () => {
        const response = await axios.get(
            `${BASE_URL}/api/shipstation/webhook/drmtlgy/orders/cached`,
            {
                headers: getAuthHeaders(),
                timeout: 10000
            }
        );

        if (response.data.counts) {
            setDrmtlgyStats(response.data.counts);
            if (response.data.lastUpdated) {
                setDrmtlgyLastUpdate(new Date(response.data.lastUpdated).toLocaleString());
            }
        }
        return response.data;
    };

    const fetchKitschCachedData = async () => {
        const response = await axios.get(
            `${BASE_URL}/api/shipstation/webhook/kitsch/orders/cached`,
            {
                headers: getAuthHeaders(),
                timeout: 10000
            }
        );

        if (response.data.counts) {
            setKitschStats(response.data.counts);
            if (response.data.lastUpdated) {
                setKitschLastUpdate(new Date(response.data.lastUpdated).toLocaleString());
            }
        }
        return response.data;
    };

    const refreshData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                axios.post(`${BASE_URL}/api/shipstation/webhook/drmtlgy/orders/refresh-cache`, {}, { headers: getAuthHeaders(), timeout: 30000 }),
                axios.post(`${BASE_URL}/api/shipstation/webhook/kitsch/orders/refresh-cache`, {}, { headers: getAuthHeaders(), timeout: 30000 }),
            ]);

            setTimeout(async () => {
                await Promise.all([fetchDrmtlgyCachedData(), fetchKitschCachedData()]);
                setLoading(false);
            }, 2000);

        } catch (error) {
            setError('Failed to refresh data: ' + error.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        const initializeDashboard = async () => {
            setLoading(true);
            try {
                await Promise.all([fetchDrmtlgyCachedData(), fetchKitschCachedData()]);
                setError(null);
            } catch (error) {
                setError('Failed to load data. Please check backend connection.');
            } finally {
                setLoading(false);
            }
        };

        initializeDashboard();

        const interval = setInterval(() => {
            fetchDrmtlgyCachedData();
            fetchKitschCachedData();
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const StatCard = ({ title, value, subtitle }) => (
        <div className="col-md-6 col-lg-3 mb-4">
            <div className="card h-100 shadow-sm">
                <div className="card-body">
                    <h5 className="card-title text-muted">{title}</h5>
                    <h2 className="display-4 fw-bold">{value}</h2>
                    <p className="card-text text-muted">{subtitle}</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="dashboard-container">
            {/* Header */}
            <div className="dashboard-header bg-primary text-white p-4 mb-4">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="h3 mb-0">Order Dashboard</h1>
                            <p className="mb-0 small">ShipStation monitoring for DRMTLGY &amp; Kitsch</p>
                        </div>
                        <button
                            className="btn btn-light btn-sm"
                            onClick={refreshData}
                            disabled={loading}
                        >
                            {loading ? 'Refreshing...' : 'Refresh Data'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Error Alert */}
                {error && (
                    <div className="alert alert-warning alert-dismissible fade show mb-4" role="alert">
                        <strong>Note:</strong> {error}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setError(null)}
                        ></button>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="alert alert-info mb-4">
                        <div className="d-flex align-items-center">
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading order data...
                        </div>
                    </div>
                )}

                {/* DRMTLGY Section */}
                <section className="mb-5">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="h4 mb-0">DRMTLGY — ShipStation Orders</h2>
                        {drmtlgyLastUpdate && (
                            <small className="text-muted">Last updated: {drmtlgyLastUpdate}</small>
                        )}
                    </div>
                    <div className="row">
                        <StatCard title="Awaiting Shipment" value={drmtlgyStats.awaitingShipment} subtitle="Orders ready to ship" />
                        <StatCard title="On Hold" value={drmtlgyStats.onHold} subtitle="Orders pending review" />
                        <StatCard title="Shipped" value={drmtlgyStats.shipped} subtitle="Orders in transit" />
                        <StatCard title="Cancelled" value={drmtlgyStats.cancelled} subtitle="Cancelled orders" />
                    </div>
                </section>

                <hr className="mb-5" />

                {/* Kitsch Section */}
                <section className="mb-5">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="h4 mb-0">Kitsch — ShipStation Orders</h2>
                        {kitschLastUpdate && (
                            <small className="text-muted">Last updated: {kitschLastUpdate}</small>
                        )}
                    </div>
                    <div className="row">
                        <StatCard title="Awaiting Shipment" value={kitschStats.awaitingShipment} subtitle="Orders ready to ship" />
                        <StatCard title="On Hold" value={kitschStats.onHold} subtitle="Orders pending review" />
                        <StatCard title="Shipped" value={kitschStats.shipped} subtitle="Orders in transit" />
                        <StatCard title="Cancelled" value={kitschStats.cancelled} subtitle="Cancelled orders" />
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Dashboard;
